import {useDataStore} from '@/store/data.store.js'
import {organizationApi} from '@/api/organization.api.js'
import {storeToRefs} from 'pinia'
import {ref, computed} from 'vue'

//Loading flag is global for all composable instances
const isLoadingOrganization = ref(false)

/**
 * Current organization composable
 */
export function useCurrentOrganization() {

  //Get store
  const store = useDataStore()
  const {organization} = storeToRefs(store)
  const {setOrganization} = store

  //Load organization
  const loadOrganization = async (force = false) => {

    //Already loaded, we don't need to load again unless forcing to reload
    if (organization.value && !force) {
      return
    }

    //Already loading
    if (isLoadingOrganization.value) {
      return
    }

    //Load org
    try {
      isLoadingOrganization.value = true
      const loadedOrganization = await organizationApi.getCurrent()

      //NOTE: There seems to be a delay between setting via the store setter,
      //and manually setting the value of the ref, which is causing problems
      //for the global resolve hook. Setting the ref value manually for now
      //seems to work around this issue.
      organization.value = loadedOrganization
      setOrganization(loadedOrganization)
    }
    finally {
      isLoadingOrganization.value = false
    }
  }

  //Clear organization
  const clearOrganization = () => setOrganization(null)

  //Expose organization data as refs
  const organizationId = computed(() => organization.value?.id)
  const organizationName = computed(() => organization.value?.name)
  const organizationLogoUrl = computed(() => organization.value?.signedImageUrl)
  const isNudgesEnabled = computed(() => organization.value?.isNudgesEnabled)
  const allowUsersToManageEnrollments = computed(() => organization.value?.allowUsersToManageEnrollments)
  const showTeamsAppVersionWarning = computed(() => organization.value?.showTeamsAppVersionWarning)
  const showMessengerHelpMenuItem = computed(() => organization.value?.showMessengerHelpMenuItem)
  const showMessengerEnrollMenuItem = computed(() => organization.value?.showMessengerEnrollMenuItem)
  const showMessengerFeedbackMenuItem = computed(() => organization.value?.showMessengerFeedbackMenuItem)
  const messengerFeedbackUrl = computed(() => organization.value?.messengerFeedbackUrl)
  const teamsAppVersion = computed(() => organization.value?.teamsAppVersion)

  //Expose interface
  return {

    //Refs
    organization,
    organizationId,
    organizationName,
    organizationLogoUrl,
    isLoadingOrganization,
    isNudgesEnabled,
    allowUsersToManageEnrollments,
    showTeamsAppVersionWarning,
    showMessengerHelpMenuItem,
    showMessengerEnrollMenuItem,
    showMessengerFeedbackMenuItem,
    messengerFeedbackUrl,
    teamsAppVersion,

    //Methods
    loadOrganization,
    clearOrganization,
  }
}
